/**
 * HAVING-style filtering for aggregated (group-by) reports.
 *
 * In group-by mode a filter on an aggregated metric column (e.g. Sum) should
 * filter the *group total* the user sees — not the raw source documents. A
 * pre-aggregation `bool.filter` would wrongly drop individual rows before the
 * sum is computed. OpenSearch's `bucket_selector` pipeline aggregation filters
 * buckets by a metric the aggregation already produced, which is exactly a SQL
 * HAVING clause.
 *
 * A filter becomes HAVING only when its field is a selected, non-group-by
 * column (so an `agg_<key>` metric exists to compare against). All other
 * filters stay as pre-aggregation document filters.
 */

import { parseFilterKey, type FilterOperator } from './filter-operators';
import type { FieldOption } from './opensearch-field-mapper';

/** Comparison operators supported by HAVING. */
const HAVING_OPS: ReadonlySet<FilterOperator> = new Set<FilterOperator>([
  'eq',
  'ne',
  'gt',
  'gte',
  'lt',
  'lte',
]);

export interface HavingContext {
  /** All selected columns (includes group-by dimensions). */
  columns: FieldOption[];
  /** Field keys used as group-by dimensions. */
  groupByKeys: string[];
}

export interface HavingResult {
  /** bucket_selector aggregations keyed by `having_<field>`. */
  selectors: Record<string, unknown>;
  /** Base field keys that became HAVING (skip these from WHERE clauses). */
  consumedFields: Set<string>;
}

/**
 * A field is an aggregated metric when it's a selected column that is NOT a
 * group-by dimension (group-by fields appear as bucket keys, not metrics).
 */
export function isMetricField(baseKey: string, ctx: HavingContext): boolean {
  if (ctx.groupByKeys.includes(baseKey)) return false;
  return ctx.columns.some((c) => c.key === baseKey);
}

function comparisonScript(op: FilterOperator, value: number): string | null {
  switch (op) {
    case 'gt':
      return `params.metric > ${value}`;
    case 'gte':
      return `params.metric >= ${value}`;
    case 'lt':
      return `params.metric < ${value}`;
    case 'lte':
      return `params.metric <= ${value}`;
    case 'eq':
      return `params.metric == ${value}`;
    case 'ne':
      return `params.metric != ${value}`;
    default:
      return null;
  }
}

/**
 * Build bucket_selector HAVING conditions from a flat (AND-combined) set of
 * filter values. Only numeric comparison filters on metric fields are
 * converted; everything else is left for the caller to apply as WHERE.
 *
 * `between` filters arrive as paired `__gte`/`__lte` entries and combine into
 * a single selector with an AND'd script.
 */
export function buildHavingSelectors(
  filterValues: Record<string, unknown>,
  ctx: HavingContext
): HavingResult {
  const consumedFields = new Set<string>();
  const byField = new Map<string, Array<{ op: FilterOperator; value: number }>>();

  for (const [key, value] of Object.entries(filterValues)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) continue; // in/not_in not supported for metrics

    const { baseKey, operator } = parseFilterKey(key);
    if (!HAVING_OPS.has(operator)) continue;
    if (!isMetricField(baseKey, ctx)) continue;

    const num = typeof value === 'number' ? value : Number(value);
    if (isNaN(num)) continue;

    if (!byField.has(baseKey)) byField.set(baseKey, []);
    byField.get(baseKey)!.push({ op: operator, value: num });
    consumedFields.add(baseKey);
  }

  const selectors: Record<string, unknown> = {};
  for (const [baseKey, comparisons] of byField) {
    const clauses = comparisons
      .map(({ op, value }) => comparisonScript(op, value))
      .filter((c): c is string => c !== null);
    if (clauses.length === 0) continue;

    selectors[`having_${baseKey}`] = {
      bucket_selector: {
        buckets_path: { metric: `agg_${baseKey}` },
        script: `params.metric != null && ${clauses.join(' && ')}`,
      },
    };
  }

  return { selectors, consumedFields };
}
